/**
 * Configuration and manipulation of domains.
 */
import { escapeRegExp } from 'lodash-es';
import { DEV, PROD, SIT, STAGE } from './env/constants';

/**
 * Returns a regular expression that will match all of the subdomains of the
 * passed domain. Since a domain can be any subset of a host, knowing the
 * domain is necessary.
 *
 * For example, if the domain is bloop.com, this will return a regex that will
 * match bloop.com, x.bloop.com, x.y.bloop.com, etc.
 *
 * @param domain
 */
export function regexSubdomainsOf(domain: string) {
  const escaped = escapeRegExp(domain);
  return new RegExp(`^([^.]+\\.)*${escaped}$`);
}

/**
 * Returns the subdomains of `domain` from the fully qualified domain name
 * `fqdn`.
 *
 * For example, if the domain is bloop.com and the fqdn is x.bloop.com, this
 * will return "x". If the domain is x.y.bloop.com, this will return "x.y".
 *
 * @param fqdn
 * @param domain
 */
export function subdomainFromFqdn(fqdn: string, domain: string) {
  const escapedDomain = escapeRegExp(domain);
  const domainRegex = new RegExp(`\\.?${escapedDomain}$`);
  return fqdn.replace(domainRegex, '');
}

/**
 * Returns a domain split into its component names in an array.
 *
 * @param domain
 */
export function domainToParts(domain: string) {
  return domain.split('.');
}

/**
 * Normal value access by key in an object, but will default to the dev
 * environment's value if the object doesn't have a value for the passed
 * environment.
 *
 * @param subdomains
 * @param environment
 */
function envValue(subdomains: Record<string, string>, environment: string): string {
  let subdomain = subdomains[environment];
  if (!subdomain) {
    subdomain = subdomains[DEV];
  }
  return subdomain;
}

// External Domains
// ================

export const EXTERNAL_DOMAIN = 'troweprice.com';

const externalSubdomains = {
  cloudApi: {
    [DEV]: 'api.dev',
    [SIT]: 'api.sit',
    [STAGE]: 'api.confidence',
    [PROD]: 'api.public',
  },
  cloudWeb: {
    [DEV]: 'wwwdev',
    [SIT]: 'wwwsit',
    [STAGE]: 'wwwstage',
    [PROD]: 'www',
  },
};

/**
 * Functions to return various domains related to external auth. The domains differ based on
 * execution environment.
 */
export const External = {
  cloudApiDomain(env: string, baseDomain = EXTERNAL_DOMAIN) {
    return `${envValue(externalSubdomains.cloudApi, env)}.${baseDomain}`;
  },

  cloudWebDomain(env: string, baseDomain = EXTERNAL_DOMAIN) {
    return `${envValue(externalSubdomains.cloudWeb, env)}.${baseDomain}`;
  },
};

// Internal Domains
// ================

export const INTERNAL_DOMAIN = 'awstrp.net';

const internalSubdomains = {
  cloudApi: {
    [DEV]: 'api.dev',
    [SIT]: 'api.sit',
    [STAGE]: 'api.stage',
    [PROD]: 'api.prod',
  },
};

/**
 * Functions to return various domains related to internal auth. The domains differ based on
 * execution environment.
 */
export const Internal = {
  cloudApiDomain(env: string, baseDomain = INTERNAL_DOMAIN) {
    return `${envValue(internalSubdomains.cloudApi, env)}.${baseDomain}`;
  },
};

// Localhost
// =========

const LOCALHOST = 'localhost';
const LOCALHOST_DEV_DOMAINS = [
  LOCALHOST,
  `${LOCALHOST}.${EXTERNAL_DOMAIN}`,
  `${LOCALHOST}.${INTERNAL_DOMAIN}`,
];

/**
 * Returns true if the passed hostname is a loopback address or one of the dev domains.
 *
 * @param hostname
 */
export function isLocalhost(hostname: string) {
  return hostname === '127.0.0.0' || LOCALHOST_DEV_DOMAINS.includes(hostname);
}
